import type { LoaderFunctionArgs } from '~/types/route-args';
import type { ShouldRevalidateFunction } from 'react-router';
import { data as routerData } from 'react-router';
import { querySearchServiceSearch } from '@modules/search';
import { mapESSearchResultsToTileViewModel } from '@modules-rocco/search/data-mappers/tile-article';
import type { ArticleTileViewModel } from '@rocco/ui/tile-article/view-models/article';
import { DEFAULT_SOURCE_FILTER } from '@modules/search/util/filter-url-helpers';
import { getCuratedCollectionArticlesCached } from '~/modules/curated-collection/api/get-curated-collection-articles-cached.server';

export const EDITORS_EDIT_ARTICLES_ACTION = '/actions/editors-edit-articles';

/**
 * API endpoint to fetch articles for the Editors' Edit section.
 *
 * Primary strategy: Extract articles from the latest Curated Collection newsletter.
 * Fallback strategy: Query search service for featured articles (sortBy: 'featured').
 *
 * Returns up to 12 articles as ArticleTileViewModel[].
 */
export const loader = async ({ context }: LoaderFunctionArgs) => {
    const { logger } = context;

    // Primary: Get articles from the latest Curated Collection newsletter
    try {
        const articles = await getCuratedCollectionArticlesCached(context);
        return { articles: articles.slice(0, 12) };
    } catch (error) {
        logger.warning(
            'Curated collection articles failed, falling back to search service',
            { error },
        );
    }

    // Fallback: Featured articles via search service
    try {
        const searchResults = await querySearchServiceSearch({
            context,
            queryParams: {
                searchType: 'blogpost',
                visibilityType: 'directory',
                filters: {},
                size: 12,
                sortBy: 'featured',
                sourceFilter: DEFAULT_SOURCE_FILTER,
            },
        });

        // Map search results to ArticleTileViewModel
        const articles: ArticleTileViewModel[] =
            searchResults?.hits
                ?.filter((hit) => hit.PType === 'Blog')
                .map((hit) => mapESSearchResultsToTileViewModel(hit)) ?? [];

        return { articles };
    } catch (error) {
        logger.error('Fallback search service also failed for articles', {
            error,
        });
        return routerData({ articles: [] }, { status: 500 });
    }
};

export const shouldRevalidate: ShouldRevalidateFunction = () => {
    return false;
};
